<template>
  <div class="page-wrapper">
    <loading :active.sync="isLoading" :is-full-page="fullPage" loader="bars" />

    <!-- ── Add-device workflow ───────────────────────────────────────── -->
    <AddDevice
      v-if="showModal"
      :show.sync="showModal"
      @closeDeviceModal="closeModal"
    />
    <CongratulationsModal
      v-if="congratulationsModal"
      :show="congratulationsModal"
      :deviceTag="newDeviceTag"
      @close="congratulationsModal = false"
    />

    <CreateSession
      v-if="showCreateSession"
      :show="showCreateSession"
      @closeCreateSessionModal="showCreateSession = false"
      @showSuccessPopup="fetchSessions"
    />

    <SuccessModal
      :show="showSuccessPopup"
      @close="closeSuccess"
      :msg="$t('success_msg_session')"
      :heading="$t('success_heading_session')"
    />

    <!-- ── Tab header (Overview / Sessions) ──────────────────────────── -->
    <div class="page-breadcrumb">
      <div class="row">
        <div class="col-md-5 col-lg-5 align-self-center col-12">
          <ul class="breadcrumb mb-2 list-style-none" style="margin-top: 1rem">
            <li class="h6 font-16 mylink" style="margin-right: 3rem">
              <router-link class="mylink" to="/devices/device-overview">
                {{ $t("overview") }}
              </router-link>
            </li>
            <li
              class="h6 font-16 mylink"
              :class="{
                active: ['DeviceSessions', 'DeviceSessionHistory'].includes(
                  $route.name
                ),
              }"
              style="margin-right: 3rem"
            >
              <router-link class="mylink" to="/devices/device-sessions" exact>
                {{ $t("sessions") }}
              </router-link>
            </li>
          </ul>
        </div>

        <div
          class="col-md-7 col-lg-7 align-self-center col-12 justify-content-end"
          style="display: flex; margin-left: auto; gap: 0.8rem"
        >
          <!-- <div class="col">
            <div class="d-flex align-items-start justify-content-end">
              <a
                href="javascript:void(0)"
                download="devices.csv"
                @click.prevent="exportDevicesCsv"
                v-if="canPerformAction('devices', 'export')"
                ><button class="btn btn-secondary" style="margin-right: 8px">
                  {{ $t("exp-csv") }}
                </button></a
              >
              <div
                data-step="1"
                v-if="canPerformAction('devices', 'addOrEdit')"
                data-intro="<h5 style='margin-bottom: 1rem; color: #213e7a'>Add a Device</h5>Add devices and track their performances."
                class="text-center"
              >
                <button @click="addDeviceModal = true" class="btn btn-primary">
                  {{ $t("add-device") }}
                </button>
              </div>
            </div>
          </div> -->

          <!-- <div v-if="showCsvButton" class="upload_container" @click="exportCsv">
            <img src="../../assets/images/solar_download-outline.svg" />
            <span class="upload_download">{{ $t("export_csv") }}</span>
          </div> -->

          <a
            href="javascript:void(0)"
            download="devices.csv"
            @click.prevent="exportCsv"
            v-if="canPerformAction('devices', 'export')"
            ><button class="btn btn-secondary" style="margin-right: 8px">
              {{ $t("export") }}
            </button></a
          >

          <!-- Add Device -->
          <button
            v-if="btnLabel && canPerformAction('devices', 'addOrEdit')"
            class="btn btn-primary"
            @click="btnClicked"
          >
            {{ btnLabel }}
          </button>
        </div>
      </div>
    </div>

    <!-- ── Child route outlet ────────────────────────────────────────── -->
    <router-view v-slot="{ Component }">
      <component
        :is="Component"
        ref="child"
        :showModal="showModal"
        :closeModal="closeModal"
      />
    </router-view>

    <footer class="footer text-center">
      ©{{ new Date().getFullYear() }} {{ $t("all-rights") }}
    </footer>
  </div>
</template>

<script>
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import AddDevice from "../Modals/AddDevice.vue";
import CongratulationsModal from "../Modals/CongratulationsModal.vue";
import CreateSession from "../Modals/CreateSession.vue";
import SuccessModal from "../Modals/SuccessModal.vue";
import { mapGetters, mapActions } from "vuex";

export default {
  name: "DeviceSession",
  components: { Loading, AddDevice, CongratulationsModal, CreateSession, SuccessModal },
  data() {
    return {
      isLoading: false,
      fullPage: true,
      showModal: false,
      congratulationsModal: false,
      newDeviceTag: "",
      showCreateSession: false,
      showSuccessPopup: false,
    };
  },
  computed: {
    ...mapGetters(["canPerformAction"]),
    btnLabel() {
      switch (this.$route.name) {
        case "DeviceSessionOverview":
          return this.$t("add-device");
        case "DeviceSessions":
          return this.$t("create_session");
        default:
          return "";
      }
    },
    showCsvButton() {
      return this.$route.name === "DeviceSessionOverview";
    },
  },
  methods: {
    ...mapActions(["fetchSessions", "fetchSessionsCount"]),
    btnClicked() {
      switch (this.$route.name) {
        case "DeviceSessionOverview":
          this.showModal = true;
          break;
        case "DeviceSessions":
          this.showCreateSession = true;
          break;
        default:
          break;
      }
    },
    closeModal() {
      this.showModal = false;
    },
    handleSetupDevice(tag) {
      this.newDeviceTag = tag;
      this.congratulationsModal = true;
      this.closeModal();
    },

    /**
     * Refresh the Sessions list after a modal success callback.
     * Tries to call a `fetchSessions` method on the current child
     * component (Sessions.vue). If that method doesn't exist, it falls
     * back to the Vuex action so data still updates.
     */
    fetchSessions() {
      const child = this.$refs.child;
      if (child && typeof child.fetchSessions === "function") {
        child.fetchSessions();
      } else {
        // fallback—update store directly
        Promise.all([
          this.$store.dispatch("fetchSessions"),
          this.$store.dispatch("fetchSessionsCount")
        ]).then(() => {
          this.showSuccessPopup = true;
        }).catch(console.error);
      }
    },
    /**
     * Emit the correct export event by route.
     */
    exportCsv() {
      if (this.$route.name === "DeviceSessionOverview") {
        this.$root.$emit("trigger-export-devices-csv");
      } else if (this.$route.name === "DeviceSessions") {
        this.$root.$emit("trigger-export-sessions-csv");
      } else if (this.$route.name === "DeviceSessionHistory") {
        this.$root.$emit("trigger-export-session-history-csv");
      }
    },
    closeSuccess() {
      this.showSuccessPopup = false;
    },
  },
};
</script>

<style scoped>
.mylink {
  color: #5e6a79 !important;
  font-size: 14px !important;
}
.mylink:hover,
.router-link-exact-active.router-link-active,
.active {
  color: #213e7a !important;
  text-decoration: underline;
  text-underline-offset: 5px;
}
.upload_container {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  cursor: pointer;
}

.upload_download {
  font-size: 14px;
  font-weight: 600;
  color: rgba(33, 62, 122, 1);
}
</style>
